import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenSmartHit, useSmartRecent, useSmartSearch } from './useSmartSearch';
import { SmartHitList } from './SmartHitList';
import { headerSearchEntities, orderRows, type SmartRow } from '../../utils/smartSearch';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import FloatingDropdown from './FloatingDropdown';
import './SmartSearch.css';

/**
 * The header search: results as you type in a panel as wide as the bar,
 * grouped by type (an Email group for email archive users), recent opens
 * when empty. Arrow keys move, Enter opens the
 * highlighted (or first) result, Escape closes. "/" or Ctrl/Cmd+K focuses it
 * from anywhere.
 */
export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // Enter pressed while results are still updating opens the top result once it arrives.
  const [enterPending, setEnterPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const openHit = useOpenSmartHit();
  const { hasCapability } = useMyCapabilities();
  // Email Records users also get their Gmail and Instantly emails (REG-885).
  const entities = useMemo(() => headerSearchEntities(hasCapability('view_email_archive')), [hasCapability]);
  const { hits, isFetching } = useSmartSearch(query, entities, 6, open);
  const recents = useSmartRecent(open && query.trim() === '');
  const rows = useMemo(() => orderRows(hits, query.trim() === '' ? recents : []), [hits, recents, query]);

  useEffect(() => setHighlighted(-1), [hits]);

  useEffect(() => {
    if (!enterPending || isFetching) return;
    setEnterPending(false);
    if (rows[0]) select(rows[0]);
    else if (query.trim()) navigate(`/search?query=${encodeURIComponent(query.trim())}`);
    // select/navigate are stable for this purpose; run only when loading settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterPending, isFetching, rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const select = (row: SmartRow) => {
    if (row.kind === 'hit') openHit(row.hit, query);
    else openHit(row.recent, '');
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rows.length) setHighlighted((highlighted + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rows.length) setHighlighted(highlighted <= 0 ? rows.length - 1 : highlighted - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && rows[highlighted]) select(rows[highlighted]);
      else if (isFetching) setEnterPending(true);
      else if (rows[0]) select(rows[0]);
      else if (query.trim()) navigate(`/search?query=${encodeURIComponent(query.trim())}`);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showAll = () => {
    const q = query.trim();
    setOpen(false);
    navigate(q ? `/search?query=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <div ref={boxRef} className="smart-global-search" data-testid="global-search">
      <svg className="smart-search-icon" width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
      </svg>
      <input
        ref={inputRef}
        className="smart-global-input"
        type="search"
        aria-label="Search"
        placeholder="Search merchants, deals, contacts, phone, email, #deal"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setEnterPending(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {!query && <kbd className="smart-search-kbd" aria-hidden="true">/</kbd>}
      {open && (rows.length > 0 || query.trim() !== '') && (
        <FloatingDropdown anchor={boxRef}>
        <SmartHitList
          rows={rows}
          highlighted={highlighted}
          onHighlight={setHighlighted}
          onSelect={select}
          isFetching={isFetching}
          query={query}
          footer={
            <>
              <span>↑↓ to move · Enter to open</span>
              <button type="button" className="smart-footer-link" onClick={showAll}>All results</button>
            </>
          }
        />
        </FloatingDropdown>
      )}
    </div>
  );
}
